import { useTheme } from '../context/ThemeContext';

// Base skeleton with pulse animation
export function Skeleton({ className = '', ...props }) {
  const { isDark } = useTheme();
  return (
    <div
      className={`animate-pulse rounded ${isDark ? 'bg-gray-800' : 'bg-gray-200'} ${className}`}
      {...props}
    />
  );
}

// Page wrapper skeleton
export function PageSkeleton({ children }) {
  const { isDark } = useTheme();
  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-950 text-white' : 'bg-gray-50 text-gray-900'}`}>
      {children}
    </div>
  );
}

// Header skeleton
export function HeaderSkeleton() {
  const { isDark } = useTheme();
  return (
    <header className={`sticky top-0 z-50 ${isDark ? 'bg-gray-900/80' : 'bg-white/80'} backdrop-blur-lg border-b ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
        <Skeleton className="h-8 w-32" />
        <div className="flex items-center gap-4">
          <Skeleton className="h-8 w-20 rounded-full" />
          <Skeleton className="h-10 w-10 rounded-full" />
        </div>
      </div>
    </header>
  );
}

// Landing page skeleton
export function LandingPageSkeleton() {
  const { isDark } = useTheme();
  const bgMain = isDark ? 'bg-gray-950' : 'bg-white';
  
  return (
    <PageSkeleton>
      {/* Header */}
      <header className={`${isDark ? 'bg-gray-900/80' : 'bg-white/80'} backdrop-blur-lg border-b ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Skeleton className="h-8 w-28" />
          <div className="flex items-center gap-3">
            <Skeleton className="h-8 w-20 rounded-full" />
            <Skeleton className="h-8 w-8 rounded-full" />
            <Skeleton className="h-9 w-20 rounded-full" />
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className={`${bgMain} px-4 py-8`}>
        <div className="max-w-6xl mx-auto text-center">
          <Skeleton className="h-4 w-48 mx-auto mb-4" />
          <Skeleton className="h-10 w-96 mx-auto mb-6" />
          <Skeleton className="h-12 w-80 mx-auto rounded-full" />
        </div>
      </section>

      {/* Topic Cards */}
      <section className={`${bgMain} px-4 py-8`}>
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-4">
            {[1, 2].map(i => (
              <div key={i} className={`p-6 rounded-xl ${isDark ? 'bg-gray-900' : 'bg-gray-100'}`}>
                <Skeleton className="h-12 w-12 rounded-xl mb-4" />
                <Skeleton className="h-6 w-40 mb-2" />
                <Skeleton className="h-4 w-full mb-1" />
                <Skeleton className="h-4 w-3/4" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className={`${bgMain} px-4 py-8`}>
        <div className="max-w-4xl mx-auto flex justify-center gap-12">
          {[1, 2, 3].map(i => (
            <div key={i} className="text-center">
              <Skeleton className="h-8 w-16 mx-auto mb-2" />
              <Skeleton className="h-4 w-20 mx-auto" />
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className={`${isDark ? 'bg-gray-900/50' : 'bg-gray-50'} px-4 py-12`}>
        <div className="max-w-4xl mx-auto flex justify-center gap-16">
          {[1, 2, 3].map(i => (
            <div key={i} className="text-center">
              <Skeleton className="h-12 w-12 rounded-xl mx-auto mb-3" />
              <Skeleton className="h-5 w-24 mx-auto mb-2" />
              <Skeleton className="h-4 w-32 mx-auto" />
            </div>
          ))}
        </div>
      </section>
    </PageSkeleton>
  );
}

// Dashboard skeleton
export function DashboardSkeleton() {
  const { isDark } = useTheme();
  
  return (
    <PageSkeleton>
      <HeaderSkeleton />
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Welcome section */}
        <div className="mb-8">
          <Skeleton className="h-8 w-64 mb-2" />
          <Skeleton className="h-5 w-48" />
        </div>

        {/* Stats cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className={`p-4 rounded-xl ${isDark ? 'bg-gray-900' : 'bg-white'} border ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
              <Skeleton className="h-4 w-20 mb-2" />
              <Skeleton className="h-8 w-16" />
            </div>
          ))}
        </div>

        {/* Topic cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <div key={i} className={`p-6 rounded-xl ${isDark ? 'bg-gray-900' : 'bg-white'} border ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
              <div className="flex items-start gap-4 mb-4">
                <Skeleton className="h-12 w-12 rounded-xl" />
                <div className="flex-1">
                  <Skeleton className="h-5 w-32 mb-2" />
                  <Skeleton className="h-4 w-full" />
                </div>
              </div>
              <Skeleton className="h-2 w-full rounded-full mb-4" />
              <Skeleton className="h-10 w-full rounded-full" />
            </div>
          ))}
        </div>
      </main>
    </PageSkeleton>
  );
}

// Topic detail skeleton
//
// Note: no <HeaderSkeleton /> is rendered — pages that show this skeleton
// live inside MainLayout, which already has the persistent UnifiedNavbar
// mounted above them. Rendering a fake navbar here would paint a second
// blue bar under the real one and disappear in a visible flicker the
// moment data arrives.
export function TopicDetailSkeleton() {
  const { isDark } = useTheme();

  return (
    <PageSkeleton>
      <main className="px-4 md:px-8 lg:px-12 py-6 pt-20 max-w-7xl mx-auto">
        {/* Back button */}
        <Skeleton className="h-6 w-24 mb-6" />

        {/* Topic header */}
        <div className="flex items-start gap-4 mb-8">
          <Skeleton className="h-16 w-16 rounded-xl" />
          <div className="flex-1">
            <Skeleton className="h-8 w-48 mb-2" />
            <Skeleton className="h-4 w-full mb-1" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        </div>

        {/* Stats row */}
        <div className="flex gap-4 mb-8">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="h-20 flex-1 rounded-xl" />
          ))}
        </div>

        {/* Assessment card */}
        <div className={`p-6 rounded-xl ${isDark ? 'bg-gray-900' : 'bg-white'} border ${isDark ? 'border-gray-800' : 'border-gray-200'} mb-8`}>
          <Skeleton className="h-6 w-48 mb-4" />
          <Skeleton className="h-4 w-full mb-2" />
          <Skeleton className="h-4 w-3/4 mb-4" />
          <Skeleton className="h-12 w-40 rounded-full" />
        </div>

        {/* Subtopics */}
        <Skeleton className="h-6 w-32 mb-4" />
        <div className="space-y-3">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className={`p-4 rounded-xl ${isDark ? 'bg-gray-900' : 'bg-white'} border ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between">
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-5 w-20" />
              </div>
            </div>
          ))}
        </div>
      </main>
    </PageSkeleton>
  );
}

// Test interface skeleton
export function TestInterfaceSkeleton() {
  const { isDark } = useTheme();
  
  return (
    <PageSkeleton>
      {/* Test header */}
      <header className={`${isDark ? 'bg-gray-900' : 'bg-white'} border-b ${isDark ? 'border-gray-800' : 'border-gray-200'} px-4 py-3`}>
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-8 w-24 rounded-full" />
          <Skeleton className="h-6 w-20" />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        {/* Question */}
        <div className={`p-6 rounded-xl ${isDark ? 'bg-gray-900' : 'bg-white'} mb-6`}>
          <Skeleton className="h-4 w-24 mb-4" />
          <Skeleton className="h-6 w-full mb-2" />
          <Skeleton className="h-6 w-3/4" />
        </div>

        {/* Options */}
        <div className="space-y-3 mb-8">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className={`p-4 rounded-xl ${isDark ? 'bg-gray-900' : 'bg-white'} border ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
              <div className="flex items-center gap-4">
                <Skeleton className="h-6 w-6 rounded-full" />
                <Skeleton className="h-5 w-48" />
              </div>
            </div>
          ))}
        </div>

        {/* Navigation */}
        <div className="flex justify-between">
          <Skeleton className="h-10 w-24 rounded-full" />
          <Skeleton className="h-10 w-24 rounded-full" />
        </div>
      </main>
    </PageSkeleton>
  );
}

// Results skeleton — no <HeaderSkeleton />; MainLayout already paints the navbar.
export function ResultsSkeleton() {
  const { isDark } = useTheme();

  return (
    <PageSkeleton>
      <main className="max-w-2xl mx-auto px-4 py-8 pt-20 text-center">
        <Skeleton className="h-24 w-24 rounded-full mx-auto mb-6" />
        <Skeleton className="h-8 w-48 mx-auto mb-4" />
        <Skeleton className="h-16 w-32 mx-auto mb-4" />
        <Skeleton className="h-5 w-64 mx-auto mb-8" />

        <div className="grid grid-cols-3 gap-4 mb-8">
          {[1, 2, 3].map(i => (
            <div key={i} className={`p-4 rounded-xl ${isDark ? 'bg-gray-900' : 'bg-white'}`}>
              <Skeleton className="h-8 w-12 mx-auto mb-2" />
              <Skeleton className="h-4 w-16 mx-auto" />
            </div>
          ))}
        </div>

        <div className="flex gap-4 justify-center">
          <Skeleton className="h-12 w-36 rounded-full" />
          <Skeleton className="h-12 w-36 rounded-full" />
        </div>
      </main>
    </PageSkeleton>
  );
}

// Articles list skeleton — no <HeaderSkeleton />; MainLayout already paints the navbar.
export function ArticlesListSkeleton() {
  const { isDark } = useTheme();

  return (
    <PageSkeleton>
      <main className="max-w-5xl mx-auto px-4 py-8 pt-20">
        <Skeleton className="h-10 w-48 mb-2" />
        <Skeleton className="h-5 w-72 mb-8" />

        {/* Filter bar */}
        <div className="flex gap-4 mb-8">
          <Skeleton className="h-10 w-32 rounded-full" />
          <Skeleton className="h-10 w-32 rounded-full" />
          <Skeleton className="h-10 w-32 rounded-full" />
        </div>

        {/* Article cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className={`p-5 rounded-xl ${isDark ? 'bg-gray-900' : 'bg-white'} border ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
              <div className="flex items-center gap-2 mb-3">
                <Skeleton className="h-5 w-16 rounded-full" />
                <Skeleton className="h-4 w-12" />
              </div>
              <Skeleton className="h-6 w-full mb-2" />
              <Skeleton className="h-4 w-full mb-1" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          ))}
        </div>
      </main>
    </PageSkeleton>
  );
}

// Leaderboard skeleton — no <HeaderSkeleton />; MainLayout already paints the navbar.
export function LeaderboardSkeleton() {
  const { isDark } = useTheme();

  return (
    <PageSkeleton>
      <main className="max-w-4xl mx-auto px-4 py-8 pt-20">
        <Skeleton className="h-10 w-48 mb-8" />

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="h-10 w-24 rounded-full" />
          ))}
        </div>

        {/* Top 3 */}
        <div className="flex justify-center gap-4 mb-8">
          {[1, 2, 3].map(i => (
            <div key={i} className={`p-4 rounded-xl ${isDark ? 'bg-gray-900' : 'bg-white'} text-center`} style={{ width: i === 2 ? '140px' : '120px' }}>
              <Skeleton className="h-16 w-16 rounded-full mx-auto mb-2" />
              <Skeleton className="h-5 w-20 mx-auto mb-1" />
              <Skeleton className="h-6 w-12 mx-auto" />
            </div>
          ))}
        </div>

        {/* List */}
        <div className="space-y-2">
          {[4, 5, 6, 7, 8].map(i => (
            <div key={i} className={`p-4 rounded-xl ${isDark ? 'bg-gray-900' : 'bg-white'} flex items-center gap-4`}>
              <Skeleton className="h-8 w-8 rounded-full" />
              <Skeleton className="h-10 w-10 rounded-full" />
              <Skeleton className="h-5 w-32" />
              <Skeleton className="h-5 w-16 ml-auto" />
            </div>
          ))}
        </div>
      </main>
    </PageSkeleton>
  );
}

export default Skeleton;
